import React, { useMemo, useState } from 'react';
import { ListPage } from '../../../components/crud/ListPage';
import { DataTable } from '../../../components/ui/data-table';
import { StatCard } from '../../../components/ui/stat-card';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/use-toast';
import { authenticatedApi } from '../../../utils/authenticatedApi';
import { useTable } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Laptop, Wifi, WifiOff, Eye, Edit, Shield, Monitor, Link2, Download, Trash2, AlertTriangle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

const statusMap: Record<string, string> = { pending_pair: 'warning', active: 'success', inactive: 'default', maintenance: 'warning', error: 'error', offline: 'error', provisioning: 'processing' };

export const ConnectorDevicesList: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { tableQueryResult: { data: tableData, isLoading, refetch } } = useTable({ resource: 'devices/connectors', syncWithLocation: false });
  const devices = useMemo(() => (tableData?.data || []) as any[], [tableData]);

  const [claimOpen, setClaimOpen] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);

  const [delTarget, setDelTarget] = useState<any | null>(null);
  const [delConfirm, setDelConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const delMatches = !!delTarget && delConfirm.trim() === delTarget.serial_number;

  const doClaim = async () => {
    const code = claimCode.trim().toUpperCase();
    if (!code) { toast({ variant: 'destructive', title: 'Enter the pairing code shown on the connector' }); return; }
    setClaiming(true);
    try {
      await authenticatedApi.post('/v1/lablink/connector/claim/', { pairing_code: code });
      toast({ title: 'Connector paired', description: 'The connector is now active and will start syncing.' });
      setClaimOpen(false); setClaimCode(''); refetch();
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Pairing failed', description: 'Code invalid or expired.' });
    } finally { setClaiming(false); }
  };

  const downloadConfig = async (row: any) => {
    try {
      const res = await authenticatedApi.get(`/v1/devices/connectors/${row.id}/provisioning/`);
      const cfg = (res as any).data ?? res;
      const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'connector.config.json'; a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Config downloaded', description: 'Drop connector.config.json next to the agent and run it.' });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Could not generate config' });
    }
  };

  const doDelete = async () => {
    if (!delMatches) return;
    setDeleting(true);
    try {
      await authenticatedApi.delete(`/v1/devices/connectors/${delTarget.id}/`);
      toast({ title: 'Connector deleted', description: `${delTarget.name} and its devices were removed.` });
      setDelTarget(null); setDelConfirm(''); refetch();
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Delete failed', description: 'Could not delete this connector. Please try again.' });
    } finally { setDeleting(false); }
  };

  // Auto-refresh
  React.useEffect(() => { const i = setInterval(() => refetch(), 60000); return () => clearInterval(i); }, [refetch]);

  const health = useMemo(() => {
    const total = devices.length;
    const online = devices.filter(d => d.is_online).length;
    const avg = total ? Math.round(devices.reduce((s, d) => s + (d.health_score || 0), 0) / total) : 0;
    return { total, online, offline: total - online, avg };
  }, [devices]);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name', header: 'Device',
      cell: ({ row }) => (
        <div>
          <div className="flex items-center gap-2"><Laptop className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{row.original.name}</span></div>
          <div className="text-xs text-muted-foreground">{row.original.device_type_display} &bull; {row.original.serial_number}</div>
          <div className="text-[11px] text-muted-foreground">MAC: {row.original.mac_address}</div>
          {row.original.system_info?.os && <div className="text-[10px] text-muted-foreground">OS: {row.original.system_info.os}</div>}
        </div>
      ),
    },
    {
      id: 'location', header: 'Location & Network',
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.branch_name}</div>
          <div className="text-xs text-muted-foreground">{row.original.location_description}</div>
          <div className="mt-1 flex items-center gap-1">
            <code className="text-[10px] bg-muted px-1 rounded">{row.original.ip_address}</code>
            {row.original.vpn_enabled && <Badge variant="success" className="text-[10px]">VPN</Badge>}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'status', header: 'Status',
      cell: ({ row }) => (
        <div>
          <Badge variant={(statusMap[row.original.status] || 'default') as any}>{row.original.status_display}</Badge>
          <div className="mt-1 flex items-center gap-1 text-xs">
            {row.original.is_online ? <><Wifi className="h-3 w-3 text-success-700" /> Online</> : <><WifiOff className="h-3 w-3 text-destructive-700" /> Offline</>}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'health_score', header: 'Health',
      cell: ({ row }) => {
        const score = row.original.health_score ?? 0;
        return (
          <div className="min-w-[80px]">
            <Progress value={score} className={`h-2 ${score < 70 ? '[&>div]:bg-destructive' : score < 90 ? '[&>div]:bg-warning' : ''}`} />
            <span className="text-xs">{score}%</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'analyzer_count', header: 'Devices',
      cell: ({ row }) => (
        <div className="text-center">
          <div className="flex items-center justify-center gap-3">
            <span title="Analyzers"><span className="text-lg font-semibold text-primary">{row.original.analyzer_count ?? 0}</span><span className="text-[10px] text-muted-foreground ml-1">analyzers</span></span>
            <span title="Imaging / PACS devices"><span className="text-lg font-semibold text-primary">{row.original.imaging_device_count ?? 0}</span><span className="text-[10px] text-muted-foreground ml-1">imaging</span></span>
          </div>
          <div className="text-[10px] text-muted-foreground">connected</div>
        </div>
      ),
    },
    {
      id: 'security', header: 'Security',
      cell: ({ row }) => (
        <div>
          <Badge variant={row.original.security_level === 'critical' ? 'error' : row.original.security_level === 'high' ? 'warning' : 'info'}>{row.original.security_level_display}</Badge>
          <div className="text-[10px] text-muted-foreground mt-0.5">FW: {row.original.firmware_version}</div>
        </div>
      ),
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" title="Download connector config (plug & play)" onClick={() => downloadConfig(row.original)}><Download className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="View" onClick={() => navigate(`/devices/connectors/show/${row.original.id}`)}><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="Edit" onClick={() => navigate(`/devices/connectors/edit/${row.original.id}`)}><Edit className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="Delete connector" className="text-destructive-700 hover:text-destructive-700 hover:bg-destructive-100" onClick={() => { setDelTarget(row.original); setDelConfirm(''); }}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <ListPage
      title="Connector Devices"
      createButtonProps={{ onClick: () => navigate('/devices/connectors/create'), children: 'Add Connector' }}
      headerButtons={<Button variant="outline" onClick={() => setClaimOpen(true)}><Link2 className="h-4 w-4 mr-2" />Pair Connector</Button>}
    >
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pair a connector</DialogTitle>
            <DialogDescription>Enter the 6-character code shown on the connector's screen to activate it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Pairing code</Label>
            <Input autoFocus value={claimCode} onChange={e => setClaimCode(e.target.value.toUpperCase())} placeholder="7K2Q9M" maxLength={8}
                   onKeyDown={e => { if (e.key === 'Enter') doClaim(); }} className="font-mono tracking-widest text-lg" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setClaimOpen(false)}>Cancel</Button>
            <Button onClick={doClaim} disabled={claiming}>{claiming ? 'Pairing…' : 'Pair'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelConfirm(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive-700"><AlertTriangle className="h-5 w-5" />Delete connector</DialogTitle>
            <DialogDescription>This permanently removes the connector and everything attached to it.</DialogDescription>
          </DialogHeader>
          {delTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-destructive-200 bg-destructive-100 p-3 text-destructive-700">
                <div className="font-medium">{delTarget.name} <span className="font-mono text-xs">({delTarget.serial_number})</span></div>
                <ul className="mt-1 list-disc pl-5 text-[13px] space-y-0.5">
                  <li><strong>{delTarget.analyzer_count ?? 0}</strong> analyzer{delTarget.analyzer_count === 1 ? '' : 's'} and any imaging devices on this connector</li>
                  <li>All captured results, histograms, HL7 messages and transmission logs from those devices</li>
                </ul>
                <div className="mt-2 font-semibold">This action cannot be undone.</div>
              </div>
              <div className="space-y-1.5">
                <Label>Type <code className="bg-muted px-1 rounded font-mono">{delTarget.serial_number}</code> to confirm</Label>
                <Input autoFocus value={delConfirm} onChange={e => setDelConfirm(e.target.value)} placeholder={delTarget.serial_number}
                       className="font-mono" onKeyDown={e => { if (e.key === 'Enter' && delMatches) doDelete(); }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDelTarget(null); setDelConfirm(''); }}>Cancel</Button>
            <Button variant="destructive" disabled={deleting || !delMatches} onClick={doDelete}>{deleting ? 'Deleting…' : 'Delete connector'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Devices" value={health.total} icon={Monitor} tone="brand" />
        <StatCard label="Online" value={health.online} icon={Wifi} tone="success" />
        <StatCard label="Offline" value={health.offline} icon={WifiOff} tone={health.offline > 0 ? "destructive" : "success"} />
        <StatCard label="Avg Health" value={`${health.avg}%`} icon={Shield} tone="brand" />
      </div>
      <DataTable columns={columns} data={devices} isLoading={isLoading} searchKey="name" emptyMessage="No connector devices found" onRowClick={(row) => navigate(`/devices/connectors/edit/${row.id}`)} />
    </ListPage>
  );
};

export default ConnectorDevicesList;
